export interface Invoice {
  id: number;
  client_id: number;
  invoice_number: string;
  amount: number;
  currency: string;
  status: InvoiceStatus;
  billing_period_start: string;
  billing_period_end: string;
  milestone_id: number | null;
  paid_at: string | null;
  sent_at: string | null;
  items?: InvoiceItem[];
  item_notes?: string | any[];
}

export interface InvoiceItem {
  id: number;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  source?: 'auto' | 'manual';
  modified?: boolean;
}

export interface Client {
  id: number;
  name: string;
  short_name: string;
  legal_name: string | null;
  billing_details: {
    currency?: string;
    address?: string;
    taxId?: string;
    paymentTerms?: string;
  } | null;
}

export interface Company {
  id: number;
  name: string;
  legal_name: string | null;
  logo_url: string | null;
  address: string | null;
  short_name: string | null;
  bank_details: {
    account_number?: string;
    recipient_name?: string;
    bank_name?: string;
    swift_code?: string;
    location?: string;
    tax_id?: string;
  } | null;
}

export interface Project {
  id: number;
  name: string;
  type: string;
  client_id: number;
}

export type InvoiceStatus = 'draft' | 'reviewed' | 'sent' | 'paid';

export const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
];

export const statusLabels: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  reviewed: 'Reviewed',
  sent: 'Sent',
  paid: 'Paid',
};

export const statusColors: Record<InvoiceStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  reviewed: 'bg-blue-100 text-blue-700',
  sent: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-green-100 text-green-700',
};

export interface InvoiceGroup {
  periodKey: string;
  period: string;
  invoices: Invoice[];
  isCurrent: boolean;
  fromDate: Date;
  toDate: Date;
}

export interface InvoiceEditValues {
  amount: number;
  status: InvoiceStatus;
  currency: string;
  billing_period_start: string;
  billing_period_end: string;
  sent_at?: string | null;
  item_notes?: string | any[];
}

import * as z from "zod"

export const invoiceEditSchema = z.object({
  amount: z.number({
    required_error: "Amount is required.",
  }).min(1, {
    message: "Amount must be greater than 0.",
  }),
  status: z.enum(['draft', 'reviewed', 'sent', 'paid'], {
    required_error: "Status is required.",
  }),
  currency: z.string({
    required_error: "Currency is required.",
  }),
  billing_period_start: z.string({
    required_error: "Billing period start is required.",
  }),
  billing_period_end: z.string({
    required_error: "Billing period end is required.",
  }),
})

export const formSchema = z.object({
  client_id: z.number({
    required_error: "Client is required.",
  }),
});

export type InvoiceFormValues = z.infer<typeof formSchema>;

export type MissingBillingDetailsType = {
  clientId: number;
  clientName: string;
  missingFields: string[];
} | null;

export const invoiceItemSchema = z.object({
  id: z.number(),
  description: z.string().min(1, "Description is required"),
  quantity: z.number().min(0.01, "Quantity must be greater than 0"),
  rate: z.number().min(0.01, "Rate must be greater than 0"),
  amount: z.number().min(0, "Amount must be a positive number"),
});

export type InvoiceItemValues = z.infer<typeof invoiceItemSchema>;
